import { site } from './shared';
import type { LegalPageContent } from './types';

const addressBlockDe = [
  site.name,
  site.addressStreet,
  `${site.addressPostalCode} ${site.addressLocality}`,
  site.addressCountryDe,
];

export const legalNoticeDe: LegalPageContent = {
  locale: 'de',
  title: 'Impressum | Maxim Domentii',
  description: 'Impressum, Betreiberangaben und Kontaktinformationen für maximdomentii.com.',
  h1: 'Impressum',
  intro: 'Diese Seite enthält Betreiber- und Geschäftsangaben zu dieser Website.',
  sections: [
    {
      title: 'Betreiber dieser Website',
      contactBlock: [
        ...addressBlockDe,
        `E-Mail: ${site.email}`,
        `Telefon: ${site.phoneDisplay}`,
        `Website: ${site.domain}`,
        `Unternehmens-UID: ${site.uid}`,
        `MWST-Nummer: ${site.vat}`,
      ],
    },
    {
      title: 'Verantwortlich für den Inhalt',
      contactBlock: addressBlockDe,
    },
    {
      title: 'Zweck der Website',
      paragraphs: [
        'Diese Website informiert über Software Engineering, Softwarearchitektur, SaaS-Entwicklung, KI-Automatisierung und digitale Beratungsleistungen von Maxim Domentii.',
      ],
    },
    {
      title: 'Urheberrecht',
      paragraphs: [
        'Sofern nicht anders angegeben, liegen Inhalte und Gestaltung dieser Website bei Maxim Domentii. Marken, Logos und Namen Dritter bleiben Eigentum der jeweiligen Rechteinhaber.',
      ],
    },
    {
      title: 'Kontakt',
      paragraphs: [`Bei Fragen zu dieser Website wenden Sie sich bitte an ${site.email}.`],
    },
  ],
};

export const dataProtectionDe: LegalPageContent = {
  locale: 'de',
  title: 'Datenschutzerklärung | Maxim Domentii',
  description:
    'Datenschutzerklärung zu Kontaktformular, reCAPTCHA, Hosting, Local Storage und Datenbearbeitung auf maximdomentii.com.',
  h1: 'Datenschutzerklärung',
  intro: 'Stand: 30. April 2026',
  sections: [
    {
      title: '1. Verantwortlicher',
      paragraphs: ['Verantwortlich für die Datenbearbeitung auf dieser Website ist:'],
      contactBlock: [...addressBlockDe, `E-Mail: ${site.email}`, `Telefon: ${site.phoneDisplay}`],
    },
    {
      title: '2. Geltungsbereich',
      paragraphs: [
        'Diese Datenschutzerklärung informiert darüber, wie Personendaten bearbeitet werden, wenn Sie maximdomentii.com besuchen, das Kontaktformular nutzen oder mit Maxim Domentii im Zusammenhang mit den auf dieser Website angebotenen Leistungen kommunizieren.',
      ],
    },
    {
      title: '3. Bearbeitete Personendaten',
      paragraphs: ['Diese Website bearbeitet die folgenden Personendaten in den unten beschriebenen Fällen:'],
      list: [
        'Kontaktformulardaten: Name, E-Mail-Adresse, Telefonnummer wenn eingegeben, Projektnachricht, reCAPTCHA-Token und Übermittlungsmetadaten, die durch das Website-Backend verarbeitet werden.',
        'Kommunikationsdaten: Angaben, die Sie per E-Mail, Telefon oder in anderer direkter Kommunikation mitteilen.',
        'Technische Zugriffsdaten: IP-Adresse, Browsertyp, Geräteinformationen, Betriebssystem, aufgerufene Seiten, Datum und Uhrzeit des Zugriffs, Referrer-URL und Server-Logdaten, die durch die Hosting-Infrastruktur verarbeitet werden.',
        'Sicherheitsprüfungsdaten: Informationen, die über Google reCAPTCHA v2 zur Prüfung der Sicherheitsabfrage und zum Schutz vor Spam und Missbrauch verarbeitet werden.',
        'Präferenzdaten: ausgewählte Sprache, die im Local Storage unter dem Sprachpräferenz-Schlüssel der Website gespeichert wird.',
      ],
    },
    {
      title: '4. Zwecke der Bearbeitung',
      paragraphs: ['Personendaten werden zu folgenden Zwecken bearbeitet:'],
      list: [
        'Beantwortung von Anfragen und Projektanfragen;',
        'Vorbereitung und Erbringung von Software Engineering, Softwarearchitektur, KI-Automatisierung und Beratungsleistungen;',
        'Betrieb, Sicherheit und Verbesserung der Website;',
        'Verhinderung von Spam, Missbrauch und unberechtigtem Zugriff;',
        'Einhaltung gesetzlicher Pflichten und Wahrung rechtlicher Ansprüche, soweit erforderlich.',
      ],
    },
    {
      title: '5. Kontaktformular',
      paragraphs: [
        'Wenn Sie das Kontaktformular absenden, werden die von Ihnen angegebenen Daten an das Website-Backend übermittelt und zur Bearbeitung Ihrer Anfrage verwendet. Das Frontend sendet nur die Felder name, email, phone, text und den reCAPTCHA-Token an die Kontakt-API. Das Formular erfordert einen reCAPTCHA-Verifizierungstoken, um Spam und Missbrauch zu reduzieren. Bitte übermitteln Sie keine vertraulichen oder besonders schützenswerten Informationen über das Kontaktformular.',
      ],
    },
    {
      title: '6. Google reCAPTCHA',
      paragraphs: [
        'Diese Website verwendet Google reCAPTCHA v2 (Checkbox), um das Kontaktformular vor automatisiertem Missbrauch zu schützen. Das reCAPTCHA-Widget wird für das Kontaktformular geladen, wenn sich das Formular in der Nähe des sichtbaren Bereichs befindet oder wenn eine Nutzerin oder ein Nutzer das Formular fokussiert. Google verarbeitet technische und nutzungsbezogene Informationen, zum Beispiel IP-Adresse, Browser- und Geräteinformationen, Interaktionssignale und weitere Daten, die für die Sicherheitsprüfung erforderlich sind. Welche Daten im Einzelnen verarbeitet werden, wird durch die Implementierung von Google und die Browserumgebung bestimmt.',
      ],
    },
    {
      title: '7. Hosting und technische Infrastruktur',
      paragraphs: [
        'Diese Website wird als statische Website über Amazon Web Services in der AWS Europe (Zurich) Region betrieben. Amazon S3 wird für die Speicherung der statischen Website verwendet und Amazon CloudFront für die Auslieferung von Inhalten. AWS und zugehörige technische Infrastruktur-Anbieter bearbeiten technische Zugriffsdaten und Server-Logs, um die Website sicher und zuverlässig bereitzustellen.',
      ],
    },
    {
      title: '8. Cookies, Local Storage und ähnliche Technologien',
      paragraphs: [
        'Diese Website verwendet keine Analytics-Cookies, Marketing-Cookies, Tracking-Pixel oder Werbe-Tags. Sie verwendet Local Storage, um die ausgewählte Sprache zu speichern, damit Besucherinnen und Besucher zur bevorzugten Sprachversion zurückkehren können.',
        'Google reCAPTCHA sowie Hosting- oder Sicherheitsdienste verwenden Cookies oder ähnliche Technologien, die für Spam-Schutz, Missbrauchsprävention, Sicherheit und zuverlässige Auslieferung der Website erforderlich sind.',
      ],
    },
    {
      title: '9. Bekanntgabe an Dritte',
      paragraphs: [
        'Personendaten werden an Dienstleister weitergegeben, die für Website-Betrieb, Hosting, Spam-Schutz, Kommunikation, Wartung und gesetzliche Pflichten erforderlich sind. Dazu gehören Amazon Web Services für Hosting und Inhaltsauslieferung, Google reCAPTCHA für Spam-Schutz sowie E-Mail- oder Kommunikationsanbieter, die zur Beantwortung von Anfragen verwendet werden.',
      ],
    },
    {
      title: '10. Übermittlung ins Ausland',
      paragraphs: [
        'Die statischen Website-Dateien werden in der Schweiz in der AWS Europe (Zurich) Region gespeichert und aus dieser Region als Website-Origin bereitgestellt. Andere für diese Website eingesetzte Dienstleister, einschliesslich Google reCAPTCHA, Amazon-CloudFront-Edge-Infrastruktur sowie E-Mail- oder Kommunikationsanbieter, bearbeiten Daten in der Schweiz, im Europäischen Wirtschaftsraum, in den USA und in anderen Ländern. Übermittlungen in Länder ohne angemessenes Datenschutzniveau erfolgen mit den nach anwendbarem Recht erforderlichen Garantien, zum Beispiel vertraglichen Schutzmechanismen der jeweiligen Dienstleister.',
      ],
    },
    {
      title: '11. Aufbewahrung',
      paragraphs: [
        'Personendaten werden nur so lange aufbewahrt, wie es für die beschriebenen Zwecke notwendig ist, sofern keine längere Aufbewahrung gesetzlich vorgeschrieben oder erlaubt ist. Kontaktanfragen werden für Nachbearbeitung, Geschäftsadministration und rechtliche Dokumentation aufbewahrt. Technische Logs werden für einen begrenzten Zeitraum aus Sicherheits- und Betriebsgründen gespeichert.',
      ],
    },
    {
      title: '12. Datensicherheit',
      paragraphs: [
        'Es werden angemessene technische und organisatorische Massnahmen eingesetzt, um Personendaten vor unberechtigtem Zugriff, Verlust, Missbrauch oder Veränderung zu schützen. Eine vollständige Sicherheit bei Übertragung im Internet oder elektronischer Speicherung kann nicht garantiert werden.',
      ],
    },
    {
      title: '13. Ihre Rechte',
      paragraphs: [
        'Nach anwendbarem Datenschutzrecht haben Sie Rechte in Bezug auf Ihre Personendaten, einschliesslich Auskunft, Berichtigung unrichtiger Daten, Löschung, Einschränkung der Bearbeitung, Widerspruch gegen die Bearbeitung sowie Datenübertragbarkeit. Zudem haben Sie, soweit anwendbar, das Recht, sich bei einer zuständigen Datenschutzbehörde zu beschweren.',
        `Für Anfragen kontaktieren Sie bitte: ${site.email}.`,
      ],
    },
    {
      title: '14. Automatisierte Entscheidungen',
      paragraphs: [
        'Diese Website trifft keine automatisierten Einzelentscheidungen mit rechtlicher Wirkung oder ähnlich erheblicher Auswirkung. Google reCAPTCHA beurteilt automatisiert, ob eine Formularübermittlung zu Sicherheitszwecken legitim erscheint.',
      ],
    },
    {
      title: '15. Änderungen',
      paragraphs: [
        'Diese Datenschutzerklärung wird aktualisiert, wenn Änderungen der Website, Leistungen, Technologie oder rechtlichen Anforderungen eine Anpassung erforderlich machen. Es gilt die jeweils auf dieser Seite veröffentlichte Version.',
      ],
    },
  ],
};
